import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'

import {
  CustomDropdownItemOption,
  DropdownIconComponentProps,
  DropDownItemOptionComponentProps,
  SelectDropdown,
  SelectDropdownRef,
  SelectOption,
} from '../../../components/SelectDropdown/SelectDropdown'

import { useGetAllEmailTypesQuery } from '../../../store/integrations/emailTypes'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'

import { EmailType, EmailTypeKind } from '../../../models/EmailType'
import { EmailTypeKindIcon } from '../../Setup/EmailTypes/EmailTypeView'

const _CUSTOM_EMAIL_TYPE_OPTION: SelectOption = {
  id: 'custom',
  label: 'Custom',
}

const _CUSTOM_EMAIL_TYPE_LABEL = 'Can’t find the right template?'

const _CUSTOM_EMAIL_TYPE_DESCRIPTION = 'Generate a custom email with your own instructions.'

const emailTypeToSelectOption = (emailType: EmailType | 'custom') => {
  if (emailType === 'custom') {
    return _CUSTOM_EMAIL_TYPE_OPTION
  }
  return {
    id: emailType.id,
    label: emailType.name,
  }
}

interface EmailTypeSelectorV2Props {
  onEmailTypeSelected: (emailType: EmailType | 'custom' | null) => void
  disabled?: boolean
  kind?: EmailTypeKind
  preventOpenOnMount?: boolean
}

export const EmailTypeSelectorV2: React.FC<EmailTypeSelectorV2Props> = ({ onEmailTypeSelected, disabled = false, kind, preventOpenOnMount = false }) => {
  const accountId = useAppSelector(selectAccountId)

  const dropdownRef = useRef<SelectDropdownRef>(null)

  const [selectedEmailType, setSelectedEmailType] = useState<EmailType | 'custom' | null>(null)

  const { data: emailTypeResponse, isLoading } = useGetAllEmailTypesQuery({ accountId, kind })

  useEffect(() => {
    if (preventOpenOnMount || !dropdownRef.current) {
      return
    }
    dropdownRef.current.focus()
  }, [preventOpenOnMount])

  const emailTypeOptions = useMemo(() => {
    if (!emailTypeResponse) {
      return [_CUSTOM_EMAIL_TYPE_OPTION]
    }
    return [...emailTypeResponse.data.map(emailTypeToSelectOption), _CUSTOM_EMAIL_TYPE_OPTION]
  }, [emailTypeResponse])

  const handleOptionSelected = (selectOption: SelectOption) => {
    const { id: selectedOptionId } = selectOption

    if (selectedOptionId === 'custom') {
      setSelectedEmailType('custom')
      onEmailTypeSelected('custom')
      return
    }

    if (!emailTypeResponse) {
      return
    }

    const emailType = emailTypeResponse.data.find((e) => e.id === selectedOptionId)
    if (!emailType) {
      return
    }

    setSelectedEmailType(emailType)
    onEmailTypeSelected(emailType)
  }

  const handleOptionDeselected = () => {
    setSelectedEmailType(null)
    onEmailTypeSelected(null)

    if (!dropdownRef.current) {
      return
    }
    dropdownRef.current.focus()
  }

  const EmailTypeSelectorV2DropdownItemOptionComponent: React.FC<DropDownItemOptionComponentProps> = ({ optionId, optionLabel, onClick }) => {
    if (!emailTypeResponse) {
      return null
    }

    if (optionId === 'custom') {
      return (
        <CustomDropdownItemOption role='button' onClick={onClick}>
          <div className='d-flex align-items-center zocks-gap-1 py-2'>
            <div className='d-flex align-items-center justify-content-center flex-shrink-0 rounded email-type-icon zocks-bg-color-primary-light'>
              <i className='flex-shrink-0 fs-5 zocks-text-primary-dark bi bi-card-text' />
            </div>
            <div className='d-flex flex-column'>
              <span className='paragraph-medium bold'>{_CUSTOM_EMAIL_TYPE_LABEL}</span>
              <span className='paragraph-small text-truncate'>{_CUSTOM_EMAIL_TYPE_DESCRIPTION}</span>
            </div>
          </div>
        </CustomDropdownItemOption>
      )
    }

    const emailType = emailTypeResponse.data.find((e) => e.id === optionId)

    return (
      <CustomDropdownItemOption role='button' onClick={onClick}>
        {emailType ? (
          <div className='d-flex align-items-center zocks-gap-1 py-2'>
            <EmailTypeKindIcon emailType={emailType} />
            <div className='d-flex flex-column'>
              <span className='paragraph-medium bold'>{emailType.name}</span>
              <span className='paragraph-small text-truncate'>{emailType.detectionConfig.description}</span>
            </div>
          </div>
        ) : (
          optionLabel
        )}
      </CustomDropdownItemOption>
    )
  }

  return (
    <div className='d-flex align-items-center'>
      <Form.Label className='new-email-selector-label paragraph-medium flex-shrink-0'>Email type:</Form.Label>
      <SelectDropdown
        ref={dropdownRef}
        className='border-0'
        searchInputClassName='new-email-selector-search-input'
        options={emailTypeOptions}
        initialSelectedOption={selectedEmailType ? emailTypeToSelectOption(selectedEmailType) : null}
        DropDownItemOptionComponent={EmailTypeSelectorV2DropdownItemOptionComponent}
        IconComponent={EmailTypeSelectorV2IconComponent}
        onOptionSelected={handleOptionSelected}
        placeholder='Pick an Email Type'
        hideClearButton={true}
        hideArrows={true}
        searchable={true}
        disabled={disabled}
        loading={isLoading}
      />
      {!disabled && selectedEmailType ? (
        <div className='new-email-selector-clear-icon-wrapper d-flex align-items-center justify-content-center flex-shrink-0'>
          <i className='bi bi-x-lg cursor-pointer' onClick={handleOptionDeselected} />
        </div>
      ) : null}
    </div>
  )
}

const EmailTypeSelectorV2IconComponent: React.FC<DropdownIconComponentProps> = ({ selectedOption }) => {
  if (!selectedOption) {
    return null
  }

  const _className = 'email-type-selector-v2-icon-component-wrapper d-flex align-items-center justify-content-center flex-shrink-0 text-black'

  const { id } = selectedOption

  if (id === 'custom') {
    return (
      <div className={_className}>
        <i className='bi bi-stars' />
      </div>
    )
  }

  return (
    <div className={_className}>
      <i className='bi bi-envelope' />
    </div>
  )
}
